/**
 * Document status labels + tooltips by document type (M3-005).
 *
 * Backend enum (canonical): Draft | In Review | Approved | Finalized
 * UI labels vary by type:
 *   Academic Student/Professional → Draft, Review, Final, Submitted
 *   Honors / Master's / Dissertation → Draft, Review, Approved, Submitted
 */

export const BACKEND_STATUSES = ['Draft', 'In Review', 'Approved', 'Finalized'];

/** Canonical workflow stages shared across types */
export const STATUS_STAGE = {
	DRAFT: 'draft',
	REVIEW: 'review',
	READY: 'ready', // Final (papers) or Approved (theses)
	SUBMITTED: 'submitted',
};

const PAPER_STATUSES = [
	{
		label: 'Draft',
		stage: STATUS_STAGE.DRAFT,
		backend: 'Draft',
		tooltip: 'Actively being written or revised.',
	},
	{
		label: 'Review',
		stage: STATUS_STAGE.REVIEW,
		backend: 'In Review',
		tooltip: 'Shared with peers, tutors, or writing centers for feedback.',
	},
	{
		label: 'Final',
		stage: STATUS_STAGE.READY,
		backend: 'Approved',
		tooltip: 'Complete and ready for submission.',
	},
	{
		label: 'Submitted',
		stage: STATUS_STAGE.SUBMITTED,
		backend: 'Finalized',
		tooltip: 'Submitted for grading.',
	},
];

const PROFESSIONAL_STATUSES = [
	{
		label: 'Draft',
		stage: STATUS_STAGE.DRAFT,
		backend: 'Draft',
		tooltip: 'In development or under revision.',
	},
	{
		label: 'Review',
		stage: STATUS_STAGE.REVIEW,
		backend: 'In Review',
		tooltip: 'Shared with co-authors or collaborators for feedback.',
	},
	{
		label: 'Final',
		stage: STATUS_STAGE.READY,
		backend: 'Approved',
		tooltip: 'Prepared according to journal or conference guidelines.',
	},
	{
		label: 'Submitted',
		stage: STATUS_STAGE.SUBMITTED,
		backend: 'Finalized',
		tooltip: 'Submitted for publication consideration',
	},
];

const HONORS_STATUSES = [
	{
		label: 'Draft',
		stage: STATUS_STAGE.DRAFT,
		backend: 'Draft',
		tooltip: 'Actively being written or revised.',
	},
	{
		label: 'Review',
		stage: STATUS_STAGE.REVIEW,
		backend: 'In Review',
		tooltip: 'Shared with advisor or honors committee for feedback.',
	},
	{
		label: 'Approved',
		stage: STATUS_STAGE.READY,
		backend: 'Approved',
		tooltip: 'Approved by advisor or honors committee.',
	},
	{
		label: 'Submitted',
		stage: STATUS_STAGE.SUBMITTED,
		backend: 'Finalized',
		tooltip: 'Submitted to the honors program or institution.',
	},
];

const GRADUATE_STATUSES = [
	{
		label: 'Draft',
		stage: STATUS_STAGE.DRAFT,
		backend: 'Draft',
		tooltip: 'Actively being written or revised.',
	},
	{
		label: 'Review',
		stage: STATUS_STAGE.REVIEW,
		backend: 'In Review',
		tooltip: 'Shared with advisor or committee for feedback.',
	},
	{
		label: 'Approved',
		stage: STATUS_STAGE.READY,
		backend: 'Approved',
		tooltip: 'Approved by an advisor or committee. Ready for submission.',
	},
	{
		label: 'Submitted',
		stage: STATUS_STAGE.SUBMITTED,
		backend: 'Finalized',
		tooltip: 'Submitted to the institution.',
	},
];

/** @deprecated Use getStatusesForDocumentType — kept for callers that expect a flat list */
export const ALL_STATUSES = ['Draft', 'Review', 'Final', 'Approved', 'Submitted'];

/**
 * Classify document type name into a status profile key.
 * @param {string} [documentType]
 * @returns {'student'|'professional'|'honors'|'masters'|'dissertation'|'default'}
 */
export function resolveDocumentStatusProfile(documentType = '') {
	const n = String(documentType || '').toLowerCase();
	if (!n.trim()) return 'default';

	if (/honors?\s*thesis|undergraduate\s*honors/.test(n)) return 'honors';
	if (/master'?s?\s*thesis|masters?\s*thesis|thesis\s*latest/.test(n)) return 'masters';
	if (/dissertation|doctoral|phd/.test(n)) return 'dissertation';

	if (
		(/academic|research|paper|manuscript/.test(n) && /professional|journal|conference/.test(n)) ||
		/professional\s*manuscript/.test(n)
	) {
		return 'professional';
	}

	if (
		(/academic|research|paper/.test(n) && /student|course|assignment/.test(n)) ||
		/academic\s*research\s*[–—-]\s*student/.test(n) ||
		(/^student/.test(n) && /paper|research/.test(n))
	) {
		return 'student';
	}

	// Broad academic paper fallbacks
	if (/professional/.test(n) && /academic|paper|manuscript|research/.test(n)) return 'professional';
	if (/student/.test(n) && /academic|paper|research/.test(n)) return 'student';
	if (/academic\s*paper|academic\s*research/.test(n) && !/professional/.test(n)) return 'student';

	return 'default';
}

function getStatusDefinitions(documentType) {
	const profile = resolveDocumentStatusProfile(documentType);
	switch (profile) {
		case 'professional':
			return PROFESSIONAL_STATUSES;
		case 'honors':
			return HONORS_STATUSES;
		case 'masters':
		case 'dissertation':
			return GRADUATE_STATUSES;
		case 'student':
			return PAPER_STATUSES;
		default:
			// Unknown types: thesis-like labels (Approved) are safer than Final
			return GRADUATE_STATUSES;
	}
}

/** Status labels available for a document type (UI order). */
export function getStatusesForDocumentType(documentType) {
	return getStatusDefinitions(documentType).map((s) => s.label);
}

/** Normalize any stored/legacy value to a backend enum value. */
export function toCanonicalStatus(status) {
	if (!status) return 'Draft';
	const s = String(status).trim();
	if (s === 'Finalized' || s === 'Submitted') return 'Finalized';
	if (s === 'In Review' || s === 'Review') return 'In Review';
	if (s === 'Final' || s === 'Approved') return 'Approved';
	if (s === 'Draft') return 'Draft';
	return s;
}

export function toStatusStage(status) {
	const canonical = toCanonicalStatus(status);
	if (canonical === 'Draft') return STATUS_STAGE.DRAFT;
	if (canonical === 'In Review') return STATUS_STAGE.REVIEW;
	if (canonical === 'Approved') return STATUS_STAGE.READY;
	if (canonical === 'Finalized') return STATUS_STAGE.SUBMITTED;
	return STATUS_STAGE.DRAFT;
}

/**
 * Map backend / legacy status → UI label for this document type.
 * @param {string} status
 * @param {string} [documentType]
 */
export function toDisplayStatus(status, documentType = '') {
	const stage = toStatusStage(status);
	const def = getStatusDefinitions(documentType).find((s) => s.stage === stage);
	return def?.label || toCanonicalStatus(status);
}

/**
 * Map UI label → backend enum for API writes.
 * @param {string} status
 * @param {string} [documentType]
 */
export function toBackendStatus(status, documentType = '') {
	const raw = String(status || '').trim();
	if (BACKEND_STATUSES.includes(raw)) return raw;

	const defs = getStatusDefinitions(documentType);
	const byLabel = defs.find((s) => s.label === raw);
	if (byLabel) return byLabel.backend;

	// Cross-type / legacy aliases
	if (raw === 'Review' || raw === 'In Review') return 'In Review';
	if (raw === 'Final' || raw === 'Approved') return 'Approved';
	if (raw === 'Submitted' || raw === 'Finalized') return 'Finalized';
	if (raw === 'Draft') return 'Draft';
	return toCanonicalStatus(raw);
}

export function getStatusTooltip(status, documentType = '') {
	const label = toDisplayStatus(status, documentType);
	const def = getStatusDefinitions(documentType).find((s) => s.label === label);
	return def?.tooltip || '';
}

/** @deprecated Prefer getStatusTooltip(status, documentType) */
export const STATUS_TOOLTIPS = {
	Draft: 'Actively being written or revised.',
	Review: 'Shared for feedback.',
	'In Review': 'Shared for feedback.',
	Final: 'Complete and ready for submission.',
	Approved: 'Approved by advisor or committee. Ready for submission.',
	Submitted: 'Submitted to the institution.',
	Finalized: 'Submitted to the institution.',
};

const TRANSITIONS_BY_STAGE = {
	[STATUS_STAGE.DRAFT]: [STATUS_STAGE.REVIEW],
	[STATUS_STAGE.REVIEW]: [STATUS_STAGE.DRAFT, STATUS_STAGE.READY],
	[STATUS_STAGE.READY]: [STATUS_STAGE.REVIEW, STATUS_STAGE.SUBMITTED],
	[STATUS_STAGE.SUBMITTED]: [STATUS_STAGE.REVIEW],
};

export function getAllowedNextStatuses(currentStatus, documentType = '') {
	const stage = toStatusStage(currentStatus);
	const nextStages = TRANSITIONS_BY_STAGE[stage] || [];
	const defs = getStatusDefinitions(documentType);
	return nextStages
		.map((s) => defs.find((d) => d.stage === s)?.label)
		.filter(Boolean);
}

/** All statuses for menus — current selectable; others enabled only when a valid transition */
export function getStatusMenuOptions(currentStatus, documentType = '') {
	const display = toDisplayStatus(currentStatus, documentType);
	const next = new Set(getAllowedNextStatuses(display, documentType));
	return getStatusesForDocumentType(documentType).map((status) => ({
		status,
		isCurrent: status === display,
		isEnabled: status === display || next.has(status),
		tooltip: getStatusTooltip(status, documentType),
	}));
}

/** Filter chip labels shown on the documents list (union of all type labels). */
export const FILTER_STATUS_LABELS = ['Draft', 'Review', 'Final', 'Approved', 'Submitted'];

export const FILTER_STATUS_TOOLTIPS = {
	Draft: 'Actively being written or revised (wording varies by document type).',
	Review: 'Shared for feedback (peers, co-authors, advisor, or committee — by type).',
	Final: 'Ready for submission — Academic Research Student/Professional only.',
	Approved: 'Approved by advisor/committee — Honors, Master’s, and Dissertation.',
	Submitted: 'Submitted for grading, publication, or to the institution — by type.',
};

/**
 * Whether a document's stored status matches a filter chip label.
 * Final and Approved both match the same backend stage (Approved).
 */
export function statusMatchesFilter(backendStatus, filterLabel) {
	if (!filterLabel) return true;
	const docStage = toStatusStage(backendStatus);
	const filterStage = (() => {
		const l = String(filterLabel).trim();
		if (l === 'Draft') return STATUS_STAGE.DRAFT;
		if (l === 'Review' || l === 'In Review') return STATUS_STAGE.REVIEW;
		if (l === 'Final' || l === 'Approved') return STATUS_STAGE.READY;
		if (l === 'Submitted' || l === 'Finalized') return STATUS_STAGE.SUBMITTED;
		return toStatusStage(l);
	})();
	return docStage === filterStage;
}

/** CSS class key for status pills (stable across Final vs Approved). */
export function getStatusStyleKey(status) {
	const stage = toStatusStage(status);
	switch (stage) {
		case STATUS_STAGE.DRAFT:
			return 'Draft';
		case STATUS_STAGE.REVIEW:
			return 'Review';
		case STATUS_STAGE.READY:
			return 'Approved'; // green “ready” style for Final and Approved
		case STATUS_STAGE.SUBMITTED:
			return 'Submitted';
		default:
			return 'Draft';
	}
}
